import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, AlertTriangle, Package, Monitor, BarChart2, ScrollText } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Spinner } from "../components/ui/spinner";
import { getAuditLog, getBaselines, getFleetOverview, getUserSettings, getSystemStatus } from "../lib/api";

const STATUS_DOT = { healthy: "bg-emerald-500", degraded: "bg-amber-400", down: "bg-red-500", unknown: "bg-slate-400" };
const STATUS_TEXT = { healthy: "Operational", degraded: "Degraded", down: "Down", unknown: "Not checked" };

// FMP-HUB-003 (DISC-013): API, signing service and CDN, in that order. The canary
// tags each with dashboard_component; the label here is only the fallback.
export const HEALTH_COMPONENTS = [
  { name: "api", label: "Cloud API" },
  { name: "signing", label: "Signing Service" },
  { name: "cdn", label: "CloudFront CDN" },
];

// FMP-HUB-003 (TC-0009): the canary writes a snapshot every minute and the status
// service treats one older than 180 s as stale (STALE_THRESHOLD_SECONDS). The hub
// re-reads the status every 60 s and applies the same inclusive limit itself, so a
// page left open never shows a check that stopped running as Operational.
export const STATUS_REFRESH_MS = 60 * 1000;
export const STATUS_STALE_AFTER_MS = 180 * 1000;

// Each part of a /system-status body the hub cannot use, as a log line (TC-0010).
export function statusProblems(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return ["body=unusable"];
  const out = [];
  if (Number.isNaN(new Date(data.checked_at).getTime())) out.push("field=checked_at value=unusable");
  if (!Array.isArray(data.components)) {
    out.push(`field=components value=${data.components === undefined ? "missing" : typeof data.components}`);
    return out;
  }
  data.components.forEach((c, i) => {
    if (!c || typeof c !== "object") out.push(`field=components[${i}] value=not an object`);
    else if (!Object.prototype.hasOwnProperty.call(STATUS_TEXT, c.status)) {
      out.push(`field=status component=${String(c.name).slice(0, 40)} value=${String(c.status).slice(0, 40)}`);
    }
  });
  return out;
}

function formatLastSync(iso) {
  if (!iso) return "never";
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return "time unreadable";
  const seconds = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  return `${minutes} min ago`;
}

const ROLE_LABEL = {
  FleetAdmin: "Fleet Admin",
  ReleaseManager: "Release Manager",
  SupportEngineer: "Support Engineer",
};

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function formatDashDate() {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
}

function formatVersion(version) {
  if (!version) return null;
  return String(version).startsWith("v") ? String(version) : `v${version}`;
}

function pickLatestByDate(items, fields) {
  return [...items].sort((left, right) => {
    const leftDate = fields.map((field) => left?.[field]).find(Boolean);
    const rightDate = fields.map((field) => right?.[field]).find(Boolean);
    return new Date(rightDate || 0).getTime() - new Date(leftDate || 0).getTime();
  })[0] || null;
}

// A fleet-overview count is only trusted when it is a genuine, non-negative,
// finite number. Anything else (missing, NaN, a negative value) is treated as
// unavailable rather than coerced to 0 - a real outage or a malformed response
// must never be indistinguishable from "0 devices need attention".
export function toValidCount(raw) {
  if (raw === undefined || raw === null) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.trunc(n);
}

function fmtCount(n) {
  return n === null ? "—" : n;
}

// -- Live fleet counts (FMP-HUB-002) -------------------------------------------
// "Refreshed at least every 60 s": polled well inside the limit so request time
// never pushes a count past it, and a count older than the limit - or one whose
// latest refresh failed - is never presented as live (DISC-011, DISC-015).
export const FLEET_REFRESH_MS = 30 * 1000;
export const FLEET_STALE_AFTER_MS = 60 * 1000;
// The service's page maximum. Totals come from the fleet-wide summary; the device
// list is only used for "+N this week", which is shown when the list is complete.
const FLEET_DEVICE_LIMIT = 100;

// -- Role-scoped tiles (FMP-HUB-001, DISC-010) ----------------------------------
// Each tile is shown only to the roles its destination route admits - these
// mirror the allowedRoles of /firmware, /devices, /rollouts and /admin/audit in
// App.js, so a tile never leads to an Access Denied page.
const ALL_PORTAL_ROLES = ["FleetAdmin", "ReleaseManager", "SupportEngineer", "Nurse", "Patient"];
export const HUB_TILE_ROLES = {
  installBaseline: ["FleetAdmin", "ReleaseManager"],
  fleetManagement: ALL_PORTAL_ROLES,
  reports: ALL_PORTAL_ROLES,
  activityTrace: ["FleetAdmin", "ReleaseManager"],   // FMP-AT-001
};

function formatClock(ms) {
  return ms ? new Date(ms).toLocaleTimeString("en-US", { hour12: false }) : "never";
}

// -- Stat Card -----------------------------------------------------------------
// subIndependent: the sub line carries its own count (e.g. "51 mandatory"), so a
// missing main value must not hide it - only an unavailable sub hides itself.
function StatCard({ value, label, sub, subIcon, valueColor, onClick, subIndependent = false, stale = false }) {
  const unavailable = value === null;
  const showSub = Boolean(sub) && (subIndependent || !unavailable);
  // A last-known value is still shown when the counts are not live, but greyed
  // and titled so it cannot be read as current (DISC-015).
  const valueClass = unavailable ? "text-slate-300" : stale ? "text-slate-400" : valueColor;
  const valueTitle = unavailable ? "Data unavailable" : stale ? "Not live: last known value" : undefined;
  return (
    <div className={`relative bg-white rounded-xl border border-slate-200 px-6 py-5 ${onClick ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`}>
      {/* Same overlay-button click target as FeatureCard: keeps the tile's own
          text as plain content (not swallowed into a button's accessible name)
          while still deep-linking into the pre-filtered fleet view (WP-HUB-02). */}
      {onClick && (
        <button
          type="button"
          aria-label={`View devices: ${label}`}
          onClick={onClick}
          className="absolute inset-0 z-10 rounded-xl cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#08148C]"
        />
      )}
      <p
        className={`text-4xl font-bold ${valueClass}`}
        title={valueTitle}
      >
        {unavailable ? "—" : value}
      </p>
      <p className="text-sm text-slate-500 mt-1">
        {unavailable ? `${label} unavailable` : label}
      </p>
      {showSub && (
        <p className="text-xs mt-1 flex items-center gap-1">
          {subIcon}
          {sub}
        </p>
      )}
    </div>
  );
}

// -- Feature Card --------------------------------------------------------------
function FeatureCard({ icon: Icon, iconBg, borderColor, title, description, badge, badgeColor, tags, footer, onClick }) {
  return (
    <div
      className={`relative bg-white rounded-xl border-2 ${borderColor} p-6 flex flex-col gap-3 cursor-pointer hover:shadow-md transition-shadow`}
    >
      {/* Full-card click target. A real <button> rather than role="button" on the
          card itself: the role trips S6819, and wrapping the card in a <button>
          would swallow the <h3> below into the button's accessible name and stop
          it being a heading. An overlay keeps the heading, keeps the markup valid,
          and gets Enter/Space plus focus handling from the native element. */}
      <button
        type="button"
        aria-label={title}
        onClick={onClick}
        className="absolute inset-0 z-10 rounded-xl cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#08148C]"
      />
      <div className="flex items-start justify-between">
        <div className={`w-11 h-11 rounded-xl ${iconBg} flex items-center justify-center`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        {badge && (
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${badgeColor}`}>
            {badge}
          </span>
        )}
      </div>
      <div>
        <h3 className="font-bold text-slate-900 text-base">{title}</h3>
        <p className="text-sm text-slate-500 mt-1 leading-relaxed">{description}</p>
      </div>
      {tags && (
        <div className="flex flex-wrap gap-2">
          {tags.map((t) => (
            <span key={t.label} className={`text-xs px-3 py-1 rounded-full border ${t.style}`}>
              {t.label}
            </span>
          ))}
        </div>
      )}
      {footer}
      <div className="flex justify-end mt-auto">
        <ArrowRight className="h-4 w-4 text-slate-400" />
      </div>
    </div>
  );
}

// -- Fleet count freshness -----------------------------------------------------
// States when the counts were last refreshed, and says plainly when they are not
// live: the latest refresh failed, or no refresh has landed within the 60 s limit.
function FleetFreshness({ fleet, fleetLive, fleetAgeMs }) {
  if (fleetLive) {
    return (
      <p data-testid="fleet-freshness" className="text-xs text-slate-400 -mt-3">
        Fleet counts updated {formatClock(fleet.fetchedAt)} · refreshed every {FLEET_REFRESH_MS / 1000} s
      </p>
    );
  }
  const reason = fleet.error
    ? `the latest refresh failed at ${formatClock(fleet.errorAt)}`
    : `no refresh has succeeded for ${Math.floor((fleetAgeMs ?? 0) / 1000)} s`;
  return (
    <div data-testid="fleet-freshness" role="status"
      className="-mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700">
      <AlertTriangle className="h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
      <span>
        Fleet counts are not live: {reason}.{" "}
        {fleet.fetchedAt
          ? `Showing the last known values from ${formatClock(fleet.fetchedAt)}.`
          : "No fleet data has been received."}
      </span>
    </div>
  );
}

// -- Mandatory-update banner (FMP-HUB-004) -------------------------------------
// Raised while devices carry an overdue mandatory firmware delta, and cleared by
// the next refresh once they are updated. A count that is unknown or not live
// raises an "unavailable" warning instead - never an all-clear, and never
// "pending on 0 devices" (DISC-014, DISC-015).
function MandatoryBanner({ fleet, fleetLive, mandatory, onViewFleet }) {
  let tone;
  let title;
  let detail;
  if (!fleetLive) {
    tone = "unavailable";
    title = "Mandatory update status unavailable";
    const lastKnown = fleet.fetchedAt && mandatory !== null
      ? ` Last known at ${formatClock(fleet.fetchedAt)}: ${mandatory} device${mandatory === 1 ? "" : "s"} pending.`
      : "";
    detail = (fleet.error
      ? `The fleet overview could not be refreshed (${formatClock(fleet.errorAt)}). Overdue devices cannot be ruled out.`
      : "The fleet counts are older than 60 s. Overdue devices cannot be ruled out.") + lastKnown;
  } else if (mandatory === null) {
    tone = "unavailable";
    title = "Mandatory update status unavailable";
    detail = "The fleet overview API did not return a usable mandatory-update count. Treat as unknown, not zero.";
  } else if (mandatory > 0) {
    tone = "pending";
    title = `Mandatory update pending on ${mandatory} device${mandatory === 1 ? "" : "s"}`;
    detail = `Live value from the fleet overview status summary, updated ${formatClock(fleet.fetchedAt)}.`;
  } else {
    return null;   // live, and nothing overdue: no banner
  }

  const box = tone === "pending"
    ? "bg-amber-50 border-amber-200"
    : "bg-red-50 border-red-200";
  const icon = tone === "pending" ? "text-amber-500" : "text-red-500";
  return (
    <div data-testid="mandatory-banner" data-state={tone} role="alert"
      className={`flex items-start gap-4 border rounded-xl px-6 py-4 ${box}`}>
      <AlertTriangle className={`h-5 w-5 shrink-0 mt-0.5 ${icon}`} />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-800 text-sm">{title}</p>
        <p className="text-xs text-slate-500 mt-0.5">{detail}</p>
      </div>
      <button
        onClick={onViewFleet}
        className="shrink-0 text-sm border border-slate-300 rounded-lg px-4 py-1.5 text-slate-700 hover:bg-white transition-colors whitespace-nowrap"
      >
        View Fleet
      </button>
    </div>
  );
}

// -- Main Page -----------------------------------------------------------------
export default function DashboardPage() {
  const { user, role } = useAuth();
  const canSeeTile = (tile) => HUB_TILE_ROLES[tile].includes(role);
  // FMP-AT-001: only these roles may read the audit log, so only they see its card.
  const canReadAudit = canSeeTile("activityTrace");
  const navigate = useNavigate();
  // A tile deep-linking into the fleet view says so in navigation state (the URL
  // is unchanged), so the view's first fetch is audited as a followed hub tile by
  // the service (TC-0004 FT-004).
  const openFleetFromTile = (path, tile) => navigate(path, { state: { via: "hub_tile", tile } });
  // One record per refresh, so the counts, their age and any failure always
  // describe the same fetch. fetchedAt = last successful refresh; error/errorAt =
  // the latest attempt failed (kept until a refresh succeeds).
  const [fleet, setFleet] = useState({
    devices: [], totalDevices: null, summary: null, fetchedAt: null, error: null, errorAt: null,
  });
  const [now, setNow] = useState(() => Date.now());
  const fleetInFlightRef = useRef(false);
  const [baselines, setBaselines] = useState([]);
  const [settingsName, setSettingsName] = useState(null);
  const [auditTotalEntries, setAuditTotalEntries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [systemStatus, setSystemStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const pollStatus = () => {
      // A failed read (api.js returns null) or a body that is not a status object
      // is shown as unavailable - never as the last good result, never as healthy.
      getSystemStatus().then((data) => {
        if (cancelled) return;
        const usable = data && typeof data === "object" && !Array.isArray(data);
        const at = new Date().toISOString();
        if (!usable) console.warn(`[Dashboard] ${at} SYSTEM_STATUS_UNAVAILABLE`);
        else statusProblems(data).forEach((p) => console.warn(`[Dashboard] ${at} SYSTEM_STATUS_UNUSABLE ${p}`));
        setSystemStatus(usable ? data : { checked_at: null, stale: true, components: [], unavailable: true });
      });
    };
    pollStatus();
    const interval = setInterval(pollStatus, STATUS_REFRESH_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const refreshFleet = useCallback(async () => {
    if (fleetInFlightRef.current) return;   // a slow refresh is never stacked
    fleetInFlightRef.current = true;
    try {
      // view labels the service's MANDATORY_STATUS_VIEWED audit entry as the
      // dashboard banner (TC-0007 FT-004); the data returned is unchanged.
      const data = await getFleetOverview({ limit: FLEET_DEVICE_LIMIT, view: "dashboard" });
      // The banner's count must be a real non-negative number. Anything else is
      // shown as "unavailable" (never 0) and recorded here, once per refresh.
      const rawMandatory = data?.statusSummary?.mandatory;
      if (toValidCount(rawMandatory) === null) {
        // api.js has already mapped an absent field to null and a non-number to NaN.
        const why = rawMandatory === null || rawMandatory === undefined ? "missing"
          : typeof rawMandatory !== "number" || Number.isNaN(rawMandatory) ? "not a number"
            : `out of range (${rawMandatory})`;
        console.warn(`[Dashboard] ${new Date().toISOString()} FLEET_SUMMARY_UNUSABLE field=mandatory value=${why}`);
      }
      setFleet({
        devices: data?.devices || [],
        totalDevices: toValidCount(data?.totalDevices),
        summary: data?.statusSummary ?? null,
        fetchedAt: Date.now(),
        error: null,
        errorAt: null,
      });
    } catch (err) {
      const at = Date.now();
      // DISC-015: the failure is surfaced on the page (below) and logged with its
      // time; the service records its own refusal in Activity-Trace.
      console.error(`[Dashboard] ${new Date(at).toISOString()} FLEET_SUMMARY_UNAVAILABLE: ${err?.message || err}`);
      setFleet((prev) => ({ ...prev, error: err?.message || "Fleet overview request failed", errorAt: at }));
    } finally {
      fleetInFlightRef.current = false;
    }
  }, []);

  // FMP-HUB-002 / DISC-011: keep the counts live. Paused while the tab is hidden
  // (nobody is looking, and every fetch is an audited view); refreshed at once
  // when it becomes visible again.
  useEffect(() => {
    const poll = setInterval(() => {
      if (!document.hidden) refreshFleet();
    }, FLEET_REFRESH_MS);
    const onVisible = () => { if (!document.hidden) refreshFleet(); };
    document.addEventListener("visibilitychange", onVisible);
    // Re-evaluates the age of the counts once a second, so they stop reading as
    // live the moment they pass FLEET_STALE_AFTER_MS.
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(poll);
      clearInterval(tick);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshFleet]);

  useEffect(() => {
    Promise.allSettled([
      refreshFleet(),
      getBaselines({ page: 1, limit: 20 }).then((data) => setBaselines(data?.items || [])),
      getUserSettings().then((data) => {
        const full = data?.full_name || data?.name || "";
        if (full) setSettingsName(full.split(" ")[0]);
      }).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [refreshFleet]);

  useEffect(() => {
    if (!canReadAudit) return;
    getAuditLog({ page: 1, limit: 1 })
      .then((data) => {
        const total = Number(data?.pagination?.totalRecords);
        // An unreadable count is shown as unavailable, never as 0 entries.
        setAuditTotalEntries(Number.isFinite(total) ? total : "unavailable");
      })
      .catch(() => setAuditTotalEntries("unavailable"));
  }, [canReadAudit]);

  const firstName = settingsName || user?.first_name || user?.name?.split(" ")[0] || "there";
  const { devices, summary: fleetSummary } = fleet;
  // Live = the latest refresh succeeded and is within the 60 s limit.
  const fleetAgeMs = fleet.fetchedAt === null ? null : now - fleet.fetchedAt;
  const fleetLive = fleet.fetchedAt !== null && !fleet.error && fleetAgeMs <= FLEET_STALE_AFTER_MS;
  // Fleet-wide totals from the service summary, not the returned page of devices
  // (which is capped at the page size).
  const totalDevices = fleet.totalDevices;
  const upToDate = toValidCount(fleetSummary?.updated);
  const pending = toValidCount(fleetSummary?.pending);
  const mandatory = toValidCount(fleetSummary?.mandatory);
  const failed = toValidCount(fleetSummary?.failed);
  const deviceListComplete = totalDevices !== null && devices.length >= totalDevices;
  const updatedThisWeek = deviceListComplete ? devices.filter((d) => {
    if (!d?.lastUpdated) return false;
    const updatedAt = new Date(d.lastUpdated).getTime();
    if (Number.isNaN(updatedAt)) return false;
    return updatedAt >= (Date.now() - (7 * 24 * 60 * 60 * 1000));
  }).length : null;
  const compliance = totalDevices > 0 && upToDate !== null ? ((upToDate / totalDevices) * 100).toFixed(1) : null;
  const activeBaselines = baselines.filter((b) => ["PUBLISHED", "SIGNED"].includes(b.state));
  const latestPublished = pickLatestByDate(
    baselines.filter((b) => b.state === "PUBLISHED"),
    ["publishedAt", "uploadedAt"]
  );
  const latestSigned = pickLatestByDate(
    baselines.filter((b) => b.state === "SIGNED"),
    ["approvedAt", "uploadedAt"]
  );
  const baselineTags = [
    latestPublished?.version ? { label: `${formatVersion(latestPublished.version)} Published`, style: "bg-emerald-50 text-emerald-700 border-emerald-200" } : null,
    latestSigned?.version ? { label: `${formatVersion(latestSigned.version)} Signed`, style: "text-[#08148C] border-teal-300" } : null,
  ].filter(Boolean);

  // TC-0010: the health response is untrusted - a components value that is not a
  // list, or entries that are not objects, used to crash the whole hub. Anything
  // unusable is treated as not reported.
  const statusComponents = Array.isArray(systemStatus?.components) ? systemStatus.components : [];
  const getComponent = (name) => statusComponents.find((c) => c && typeof c === "object" && c.name === name);
  // Stale when the service says so, or when the snapshot has aged past the limit
  // since it was fetched (an unreadable time counts as stale).
  const statusCheckedMs = systemStatus?.checked_at ? new Date(systemStatus.checked_at).getTime() : NaN;
  const statusStale = Boolean(systemStatus) && (systemStatus.stale === true
    || Number.isNaN(statusCheckedMs) || now - statusCheckedMs > STATUS_STALE_AFTER_MS);

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">

      {/* Greeting */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">
          {getGreeting()}, {firstName} {String.fromCodePoint(0x1F44B)}
        </h1>
        <p className="text-slate-400 text-sm mt-1">
          {formatDashDate()} · {ROLE_LABEL[role] || role} · EMED Technologies
        </p>
      </div>

      {/* Status Bar */}
      <div className="bg-white rounded-xl border border-slate-200 px-6 py-4 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
        {HEALTH_COMPONENTS.map(({ name, label }, i) => {
          const component = getComponent(name);
          // A status that arrived without this component was not checked - never
          // "Checking…" (as if a result were on its way) and never "Operational".
          // Only the four known statuses are shown as such; anything else reads unknown.
          const recognised = component && Object.prototype.hasOwnProperty.call(STATUS_TEXT, component.status);
          const reported = recognised ? component.status : (systemStatus ? "unknown" : undefined);
          const status = statusStale ? "unknown" : reported;
          let detail;
          if (systemStatus?.unavailable) detail = "Health status unavailable: the status service could not be read";
          else if (statusStale && Number.isNaN(statusCheckedMs)) detail = "Stale: the health snapshot carries no readable check time";
          else if (statusStale) detail = `Stale: last checked ${formatLastSync(systemStatus.checked_at)} (limit ${STATUS_STALE_AFTER_MS / 1000} s)`;
          else if (component && !recognised) detail = "Unrecognised status from the health service";
          else if (component) detail = typeof component.detail === "string" ? component.detail || undefined : undefined;
          else if (systemStatus) detail = "Not reported by the health service";
          return (
            <React.Fragment key={name}>
              {i > 0 && <div className="w-px h-5 bg-slate-200 hidden sm:block" />}
              <div className="flex items-center gap-2" data-testid={`health-${name}`} title={detail}>
                <span className={`w-2 h-2 rounded-full ${STATUS_DOT[status] || "bg-slate-300"}`} />
                <span className="text-slate-400 text-xs">{typeof component?.label === "string" && component.label ? component.label : label}</span>
                <span className="font-semibold text-slate-800">{STATUS_TEXT[status] || "Checking…"}</span>
              </div>
            </React.Fragment>
          );
        })}
        <div className="ml-auto text-xs text-slate-400">
          {statusStale ? "Status stale — last sync " : "Last sync: "}{formatLastSync(systemStatus?.checked_at)}
        </div>
      </div>

      {/* Fleet count freshness (FMP-HUB-002) */}
      <FleetFreshness fleet={fleet} fleetLive={fleetLive} fleetAgeMs={fleetAgeMs} />

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          value={totalDevices}
          label="Total Fleet Devices"
          sub={updatedThisWeek === null ? null : `+${updatedThisWeek} this week`}
          subIcon={null}
          valueColor="text-[#08148C]"
          stale={!fleetLive}
          onClick={() => openFleetFromTile("/devices?status=ALL", "total_fleet_devices")}
        />
        <StatCard
          value={upToDate}
          label="Up to Date"
          sub={compliance === null ? null : `${compliance}% compliant`}
          valueColor="text-emerald-600"
          stale={!fleetLive}
          onClick={() => openFleetFromTile("/devices?status=UP_TO_DATE", "up_to_date")}
        />
        <StatCard
          value={pending}
          label="Update Pending"
          sub={mandatory === null ? "mandatory count unavailable" : `${mandatory} mandatory`}
          subIcon={<AlertTriangle className="h-3 w-3 text-amber-500" />}
          subIndependent
          valueColor="text-amber-500"
          stale={!fleetLive}
          onClick={() => openFleetFromTile("/devices?status=PENDING", "update_pending")}
        />
        <StatCard
          value={failed}
          label="Failed / Rollback"
          sub="Requires attention"
          valueColor="text-red-500"
          stale={!fleetLive}
          onClick={() => openFleetFromTile("/devices?status=FAILED_ROLLBACK", "failed_rollback")}
        />
      </div>

      {/* Feature Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {canSeeTile("installBaseline") && <FeatureCard
          icon={Package}
          iconBg="bg-emerald-400"
          borderColor="border-emerald-300"
          title="Install Baseline"
          description="Upload firmware, manage versions, sign & publish to fleet segments. Deprecate outdated baselines."
          badge={`${activeBaselines.length || 0} Active`}
          badgeColor="bg-emerald-100 text-emerald-700"
          tags={baselineTags}
          onClick={() => navigate("/firmware")}
        />}
        {canSeeTile("fleetManagement") && <FeatureCard
          icon={Monitor}
          iconBg="bg-purple-400"
          borderColor="border-purple-200"
          title="Fleet Management"
          description="Track all devices, firmware versions, update history, and per-device audit trail & app logs."
          badge={`${fmtCount(pending)} Pending`}
          badgeColor="bg-amber-100 text-amber-700"
          tags={[
            { label: `${fmtCount(pending)} awaiting update`, style: "bg-amber-50 text-amber-600 border-amber-200" },
            { label: `${fmtCount(failed)} failed`, style: "bg-red-50 text-red-600 border-red-200" },
          ]}
          onClick={() => openFleetFromTile("/devices", "fleet_management")}
        />}
        {canSeeTile("reports") && <FeatureCard
          icon={BarChart2}
          iconBg="bg-yellow-400"
          borderColor="border-yellow-300"
          title="Reports"
          description="Rollout summary by version, device compliance by serial number, and scheduled exports."
          footer={
            <div>
              <div className="w-full bg-slate-100 rounded-full h-2 mb-1">
                <div className="bg-emerald-500 h-2 rounded-full" style={{ width: `${compliance ?? 0}%` }} />
              </div>
              <p className="text-xs text-slate-400">
                {compliance === null ? "Fleet compliance unavailable" : `${compliance}% fleet compliant with v1.5.0`}
              </p>
            </div>
          }
          onClick={() => navigate("/rollouts")}
        />}
        {canReadAudit && <FeatureCard
          icon={ScrollText}
          iconBg="bg-blue-400"
          borderColor="border-blue-200"
          title="Activity Trace"
          description="Immutable, append-only audit trail. Who uploaded, published, and which device received which firmware."
          footer={
            <p className="text-sm text-[#08148C] font-medium">
              {auditTotalEntries === null
                ? "Loading entries..."
                : typeof auditTotalEntries === "number"
                  ? `${auditTotalEntries.toLocaleString()} entries · IEC 62304 compliant`
                  : "Entries unavailable"}
            </p>
          }
          onClick={() => navigate("/admin/audit")}
        />}
      </div>

      {/* Mandatory update alert (FMP-HUB-004) */}
      <MandatoryBanner
        fleet={fleet}
        fleetLive={fleetLive}
        mandatory={mandatory}
        onViewFleet={() => openFleetFromTile("/devices?status=MANDATORY", "mandatory_banner")}
      />

    </div>
  );
}
